import { React, Fragment, memo, useEffect } from 'react'
import {
  HeadingText,
  Link,
  BodyText,
  Card,
  CaptionText,
} from '@paypalcorp/pp-react'
import { Message } from '@paypalcorp/worldready-react'
import serverData from '../../../utils/server-data'

const country = serverData.country
const posBalThSgId =
  'dataErasure.preventrequset.unresolveditems_fundsinaccount_desc_sg_th_id'
const contentKeys = {
  BR: {
    negativeBalDesc:
      'dataErasure.preventrequset.unresolveditems_payments_desc_br',
    positiveBalTitle: (
      <Message id="dataErasure.preventrequset.unresolveditems_fundsinaccount_br" />
    ),
    positiveBalDes:
      'dataErasure.preventrequset.unresolveditems_fundsinaccount_desc_br',
    positiveBalLink:
      'dataErasure.preventrequset.unresolveditems_fundsinaccount_link_br',
  },
  SG: {
    positiveBalDes: posBalThSgId,
  },
  TH: {
    positiveBalDes: posBalThSgId,
  },
  ID: {
    positiveBalDes: posBalThSgId,
  },
  AU: {
    ppCreditTitle: (
      <Message id="dataErasure.preventrequset.unresolveditems_paypalcredit_au" />
    ),
  },
  default: {
    negativeBalDesc: 'dataErasure.preventrequset.unresolveditems_payments_desc',
    positiveBalTitle: (
      <Message id="dataErasure.preventrequset.unresolveditems_fundsinaccount" />
    ),
    positiveBalDes:
      'dataErasure.preventrequset.unresolveditems_fundsinaccount_desc',
    positiveBalLink:
      'dataErasure.preventrequset.unresolveditems_fundsinaccount_link',
    ppCreditTitle: (
      <Message id="dataErasure.preventrequset.unresolveditems_paypalcredit" />
    ),
  },
}

function getKey(countrycode, key) {
  if (contentKeys[countrycode]) {
    return contentKeys[countrycode][key] || contentKeys.default[key]
  }
  return contentKeys.default[key]
}

const pendingTrans = {
  title: (
    <Message id="dataErasure.preventrequset.unresolveditems_pendingtransactions" />
  ),
  description:
    'dataErasure.preventrequset.unresolveditems_pendingtransactions_desc',
  link: 'dataErasure.preventrequset.unresolveditems_pendingtransactions_link',
  linkindescription: false,
  linktoredirect: '',
  footerlink: '/myaccount/activities',
}

const unresolvedItemsMapping = {
  POS_BAL: {
    //Funds in your account, positive balance
    title: getKey(country, 'positiveBalTitle'),
    description: getKey(country, 'positiveBalDes'),
    link: getKey(country, 'positiveBalLink'),
    linkindescription: false,
    linktoredirect: '',
    footerlink: '/myaccount/money/balances/summary',
  },
  LOCK_LIM: {
    // Account Limitation
    title: <Message id="dataErasure.preventrequset.unresolveditems_account" />,
    description: 'dataErasure.preventrequset.unresolveditems_account_desc',
    link: '',
    linkindescription: true,
    linktoredirect:
      '/smarthelp/article/why-is-my-paypal-account-limited-help534',
    footerlink: '',
  },
  TECH_ERROR: {
    //Technical Error
    title: (
      <Message id="dataErasure.preventrequset.unresolveditems_techerror" />
    ),
    description: 'dataErasure.preventrequset.unresolveditems_techerror_desc',
    link: '',
    linkindescription: true,
    linktoredirect: '/us/smarthelp/contact-us',
    footerlink: '',
  },
  PEND_DEP: pendingTrans, //Pending transactions
  PEND_TR: pendingTrans, //Pending transactions
  PEND_BAL: pendingTrans, //Pending transactions
  PEND_CB: {
    //Open cases
    title: (
      <Message id="dataErasure.preventrequset.unresolveditems_opencases" />
    ),
    description: 'dataErasure.preventrequset.unresolveditems_opencases_desc',
    link: 'dataErasure.preventrequset.unresolveditems_opencases_link',
    linkindescription: false,
    linktoredirect: '',
    footerlink: '/disputes',
  },
  PP_CREDIT: {
    //Open PayPal Credit or Working Capital accounts
    title: getKey(country, 'ppCreditTitle'),
    description: 'dataErasure.preventrequset.unresolveditems_paypalcredit_desc',
    link: '',
    linkindescription: true,
    linktoredirect: '/us/smarthelp/contact-us',
    footerlink: '',
  },
  NEG_BAL: {
    //Negative Balance, Payments owed
    title: <Message id="dataErasure.preventrequset.unresolveditems_payments" />,
    description: getKey(country, 'negativeBalDesc'),
    link: '',
    linkindescription: true,
    linktoredirect: '/smarthelp/article/PCC163',
    footerlink: '',
  },
  PEND_BA: {
    //Active subscriptions or automatic payments
    title: (
      <Message id="dataErasure.preventrequset.unresolveditems_activesuscription" />
    ),
    description:
      'dataErasure.preventrequset.unresolveditems_activesuscription_desc',
    link: '',
    linkindescription: true,
    linktoredirect: '/myaccount/autopay/',
    footerlink: '',
  },
  PEND_GC: {
    //Unused gift cards
    title: (
      <Message id="dataErasure.preventrequset.unresolveditems_giftcards" />
    ),
    description: 'dataErasure.preventrequset.unresolveditems_giftcards_desc',
    link: 'dataErasure.preventrequset.unresolveditems_giftcards_link',
    linkindescription: false,
    linktoredirect: '',
    footerlink:
      '/us/smarthelp/article/how-do-i-get-and-redeem-a-gift-card-bought-from-paypal-gifts-help325',
  },
}

const preventDuplicateRequestsCards = memo(() => {
  let { unResolvedItems = [], unResolvedError = false } = serverData

  const getUnresolvedItemElemExpression = () => {
    let errorCodeFound = false
    if (unResolvedError && unResolvedItems.length) {
      for (let i = 0; i < unResolvedItems.length; i++) {
        if (unresolvedItemsMapping[unResolvedItems[i]]) {
          errorCodeFound = true
          break
        }
      }
    }
    if (!errorCodeFound) {
      unResolvedItems = []
      unResolvedItems.push('TECH_ERROR') // Default Error code
    }
  }

  useEffect(() => {
    getUnresolvedItemElemExpression()
  }, [])

  return (
    <Fragment>
      <section id="erasure-cards">
        <HeadingText size="sm" className="text-center requestForm-row">
          <Message id="dataErasure.preventrequset.error_header" />
        </HeadingText>

        <BodyText className="requestForm-row caption-text">
          <Message id="dataErasure.preventrequset.error_desc" />
        </BodyText>

        <article className="unresolved-items">
          <BodyText strong>
            <Message id="dataErasure.preventrequset.unresolveditems" />
          </BodyText>
          <section className="unresolved-cards">
            {unResolvedItems.map((item, i) => {
              return (
                unresolvedItemsMapping[item] && (
                  <Card key={i}>
                    <Card.Header>
                      <BodyText>
                        {unresolvedItemsMapping[item].title || ''}
                      </BodyText>
                    </Card.Header>
                    <Card.Content className="card-content">
                      <CaptionText className="caption-text">
                        {unresolvedItemsMapping[item].linkindescription ? (
                          <Message
                            id={unresolvedItemsMapping[item].description}
                            link={(innerMessage) => (
                              <Link
                                href={
                                  unresolvedItemsMapping[item].linktoredirect
                                }
                                size="sm"
                              >
                                {innerMessage || ''}
                              </Link>
                            )}
                          />
                        ) : (
                          <Message
                            id={unresolvedItemsMapping[item].description}
                          />
                        )}
                      </CaptionText>
                    </Card.Content>
                    <Card.Footer className="footer">
                      {unresolvedItemsMapping[item].link.length > 0 && (
                        <CaptionText>
                          <Message
                            id={unresolvedItemsMapping[item].link}
                            link={(innerMessage) => (
                              <Link
                                href={unresolvedItemsMapping[item].footerlink}
                                size="sm"
                              >
                                {innerMessage || ''}
                              </Link>
                            )}
                          />
                        </CaptionText>
                      )}
                    </Card.Footer>
                  </Card>
                )
              )
            })}
          </section>
        </article>
      </section>
    </Fragment>
  )
})

preventDuplicateRequestsCards.displayName = 'preventDuplicateRequestsCards'
export default preventDuplicateRequestsCards
